import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { cn } from '@/lib/utils';
import { Plus } from 'lucide-react';
import { forwardRef, useImperativeHandle } from 'react';
import { BeneficialOwnerCard } from './components/BeneficialOwnerCard';
import { BeneficialOwnerTypeSelector } from './components/BeneficialOwnerTypeSelector';
import { OriginOfAssetsSectionA } from './components/OriginOfAssetsSectionA';
import { useFormA } from './hooks/useFormA';
import type { FormAData, FormAHandle, FormAProps } from './types/formA.types';

// Re-export types for external use
export type { FormAData, FormAProps };

/**
 * Form A Component - Beneficial Owner Declaration
 * Refactored version with extracted components, hooks, and utilities
 */
export const FormA = forwardRef<FormAHandle, FormAProps>(
  function FormA(props, ref) {
    const { className } = props;

    // Use custom hook for all form logic
    const {
      form,
      fields,
      handleSubmit,
      handleAddBeneficialOwner,
      handleRemoveBeneficialOwner,
      triggerValidation,
      submitError,
      showBeneficialOwnersSection,
      showOriginOfAssetsOtherField,
      originOfAssetsOptions,
      tForms,
    } = useFormA(props);

    // Expose validation method to parent
    useImperativeHandle(ref, () => ({
      triggerValidation,
    }));

    return (
      <div className={cn('space-y-6', className)}>
        {submitError && (
          <Alert variant='destructive'>
            <AlertDescription>{submitError}</AlertDescription>
          </Alert>
        )}

        <Form {...form}>
          <form
            id='form-a-form'
            onSubmit={form.handleSubmit(handleSubmit)}
            className='space-y-6'
          >
            {/* Form Header */}
            <div className='text-center mb-6'>
              <h1 className='text-2xl font-bold mb-2'>{tForms('kyc.title')}</h1>
            </div>

            <OriginOfAssetsSectionA
              control={form.control}
              showOtherField={showOriginOfAssetsOtherField}
              originOfAssetsOptions={originOfAssetsOptions}
              tForms={tForms}
            />

            {/* Beneficial Owner Type Selection */}
            <BeneficialOwnerTypeSelector
              control={form.control}
              tForms={tForms}
            />

            {/* Beneficial Owners Details Section */}
            {showBeneficialOwnersSection && (
              <div className='space-y-4'>
                <h4 className='text-md font-medium'>
                  {tForms('kyc.beneficialOwner.detailsTitle')}
                </h4>

                {fields.map((field, index) => (
                  <BeneficialOwnerCard
                    key={field.id}
                    index={index}
                    control={form.control}
                    onRemove={handleRemoveBeneficialOwner}
                    canRemove={fields.length > 1}
                    tForms={tForms}
                  />
                ))}

                <Button
                  type='button'
                  variant='outline'
                  onClick={handleAddBeneficialOwner}
                  className='w-full'
                >
                  <Plus className='w-4 h-4 mr-2' />
                  {tForms('kyc.beneficialOwner.addOwnerButton')}
                </Button>
              </div>
            )}
          </form>
        </Form>
      </div>
    );
  }
);

export default FormA;
